/**
 * Copy `text` without the async Clipboard API. `navigator.clipboard` is only
 * exposed in secure contexts (https or localhost), so when Vega is served over
 * plain http (e.g. the obs frontend at http://localhost:3002) it is `undefined`
 * and `writeText` throws. Fall back to the legacy execCommand path there.
 */
function fallbackCopyText(text: string) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    if (!document.execCommand('copy')) {
      throw new Error('execCommand copy returned false');
    }
  } finally {
    document.body.removeChild(textArea);
  }
}

/**
 * Write `text` to the clipboard, returning whether it succeeded. Prefers the
 * async Clipboard API and falls back to execCommand in insecure contexts where
 * `navigator.clipboard` is unavailable. Never throws — callers branch on the
 * returned boolean to surface their own success/error UI.
 */
export async function writeTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      fallbackCopyText(text);
    }
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard', text, error);
    return false;
  }
}
